import {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  fetchCsrfToken,
  getMe,
  login as apiLogin,
  logout as apiLogout,
} from "@/api/auth";
import { setAccessToken } from "@/api/client";
import type { User } from "@/types/auth";

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /**
   * Standard login. When the server replies with ``requires_2fa`` the
   * Promise resolves to a ``LoginRequires2FA`` payload — the SPA shows
   * the challenge screen and calls ``complete2FA`` once the user types
   * a code. On full success the Promise resolves ``undefined`` and
   * the user is in.
   */
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
    mfaCode?: string
  ) => Promise<import("@/api/twoFactor").LoginRequires2FA | undefined>;
  /** Hand back the temp_token + 6-digit code from the challenge screen
   *  to finish the two-step login. */
  complete2FA: (tempToken: string, code: string) => Promise<void>;
  /** Install an access token + user object directly. Used by the demo
   *  login flow which has its own minting endpoint and carries no
   *  refresh cookie. */
  setSession: (accessToken: string, user: User) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Try to restore session on mount.
  //
  // Post Phase-9: access tokens live in an httpOnly cookie that JS
  // can't read. We always try /me; if a session cookie is present
  // we get 200 + user, otherwise 401 + we stay logged out.
  //
  // ⚠️ DO NOT call ``setAccessToken(null)`` here. That used to be a
  // no-op (a belt-and-suspenders localStorage scrub) but after the
  // iOS Safari rescue patch, ``setAccessToken`` ALSO writes / clears
  // the in-memory bearer fallback. Wiping it at every AuthProvider
  // mount would destroy a valid session on browsers where ITP
  // dropped the cross-origin cookie — bearer was the only auth,
  // page reloads zeroed it, /me 401'd, user got booted to login.
  //
  // The legacy localStorage cleanup we used to want here still
  // happens implicitly — every login / refresh / logout calls
  // ``setAccessToken`` which scrubs localStorage. There's no real
  // user left who'd land on the SPA with a pre-Phase-9 token AND
  // never trigger any of those paths, so removing the explicit
  // scrub costs nothing.
  useEffect(() => {
    // Ensure a CSRF cookie is present before the first state-changing call.
    // Failing silently is fine — login / refresh will still provision one.
    fetchCsrfToken().catch(() => {});
    getMe()
      .then(setUser)
      .catch(() => {
        // 401 here just means "not logged in" — not an error.
      })
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(
    async (email: string, password: string, rememberMe = false, mfaCode?: string) => {
      const res = await apiLogin({
        email,
        password,
        remember_me: rememberMe,
        mfa_code: mfaCode,
      });
      // Discriminate the union: if the server says we need 2FA, hand
      // the payload back to the caller so the LoginForm can show the
      // challenge screen. Otherwise we have a real TokenResponse.
      const { isLoginRequires2FA } = await import("@/api/twoFactor");
      if (isLoginRequires2FA(res)) {
        return res;
      }
      setAccessToken(res.access_token);
      setUser(res.user);
      return undefined;
    },
    []
  );

  const complete2FA = useCallback(async (tempToken: string, code: string) => {
    const { challenge2FA } = await import("@/api/twoFactor");
    const res = await challenge2FA(tempToken, code);
    setAccessToken(res.access_token);
    setUser(res.user);
  }, []);

  const setSession = useCallback((accessToken: string, u: User) => {
    setAccessToken(accessToken);
    setUser(u);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const fresh = await getMe();
      setUser(fresh);
    } catch {
      // ignore
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  // Listen for forced logout from 401 interceptor
  useEffect(() => {
    const handleForceLogout = () => {
      setUser(null);
    };
    window.addEventListener("auth:logout", handleForceLogout);
    return () => window.removeEventListener("auth:logout", handleForceLogout);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        complete2FA,
        setSession,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
